/**
 * Avisos de coisas que chegam (Configurações → Notificações): sons próprios
 * (sintetizados aqui, sem arquivos de áudio de terceiros), aviso na tela ou do
 * sistema e o título da janela piscando quando ela está em segundo plano.
 */
import type { AppSettings } from './settings.js';
import { POPOUT } from './windowMode.js';

export type SoundKind = 'message' | 'mention' | 'roll' | 'kick' | 'pvt';

let ctx: AudioContext | null = null;

/** Notas curtas (frequência em Hz, duração em s) para cada aviso. */
const TUNES: Record<SoundKind, [number, number][]> = {
  message: [
    [880, 0.08],
    [1175, 0.12],
  ],
  mention: [
    [988, 0.07],
    [1319, 0.07],
    [1568, 0.12],
  ],
  roll: [
    [330, 0.04],
    [392, 0.04],
    [523, 0.06],
  ],
  kick: [
    [440, 0.12],
    [311, 0.2],
  ],
  pvt: [
    [659, 0.08],
    [988, 0.12],
  ],
};

export function playSound(kind: SoundKind, volume = 0.6): void {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    let at = ctx.currentTime + 0.01;
    for (const [freq, dur] of TUNES[kind]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, 0.25 * volume), at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + dur + 0.02);
      at += dur * 0.9;
    }
  } catch {
    // sem Web Audio: fica sem som
  }
}

/* ------------------------------------------------------------------ título piscando */

let flashing: number | null = null;
let baseTitle = '';
let pending = 0;

export function flashTitle(text: string): void {
  if (!document.hidden && document.hasFocus()) return;
  // no app desktop, a barra de tarefas também pisca
  (window as { rpgDeskDesktop?: { flashFrame?: () => void } }).rpgDeskDesktop?.flashFrame?.();
  if (!document.hidden) return;
  pending += 1;
  if (flashing !== null) return;
  baseTitle = document.title;
  let on = false;
  flashing = window.setInterval(() => {
    on = !on;
    document.title = on ? `(${pending}) ${text}` : baseTitle;
  }, 1000);
}

function stopFlash(): void {
  if (flashing === null) return;
  window.clearInterval(flashing);
  flashing = null;
  pending = 0;
  document.title = baseTitle;
}
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) stopFlash();
  });
  window.addEventListener('focus', stopFlash);
}

/* ------------------------------------------------------------------ avisos */

export function systemNoticeAllowed(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export async function askSystemNotices(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  return (await Notification.requestPermission()) === 'granted';
}

/**
 * Mensagem direta nova. `toast` mostra o aviso na tela do RPG Desk; com a janela em
 * segundo plano, vira aviso do sistema (se permitido) e o título pisca.
 */
export function noticeMessage(settings: AppSettings, from: string, text: string, toast: (msg: string) => void, label: string): void {
  // numa aba destacada em janela própria, quem avisa é a janela principal (senão tudo sai em dobro)
  if (POPOUT) return;
  if (settings.soundMessages) playSound('message', settings.masterVolume);
  if (settings.messageNotice === 'never') return;
  if (document.hidden) {
    if (systemNoticeAllowed()) {
      try {
        new Notification(from, { body: text.slice(0, 140), tag: `dm-${from}` });
      } catch {
        // o Chrome do Android não cria avisos assim (só por service worker): fica o título piscando
      }
    }
    if (settings.flashTitle) flashTitle(label);
  } else if (settings.messageNotice === 'always') toast(`${from}: ${text.slice(0, 100)}`);
}

/** Algo da mesa (menção, conversa privada nova): som e título piscando. */
export function noticeRoom(settings: AppSettings, kind: SoundKind, label: string): void {
  if (POPOUT) return;
  const enabled = { message: settings.soundMessages, mention: settings.soundMentions, roll: settings.soundRolls, kick: settings.soundKick, pvt: settings.soundPvt }[kind];
  if (enabled) playSound(kind, settings.masterVolume);
  if (kind !== 'roll' && settings.flashTitle) flashTitle(label);
}
